'use client';

import { Button, Paper, Popper, Stack, Typography } from '@mui/material';
import { Provider, type AzureVnetConnectivityConfig } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { ResourceGroupSelect } from '@/components/selectors/resourceGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';

import { AzureVnetSelect } from './azureVnetSelect';

export const AzureVnetSelectionDialog = ({
  accountId,
  anchorEl,
  onClose,
}: {
  accountId: string;
  anchorEl: HTMLElement | null;
  onClose: (config: AzureVnetConnectivityConfig | undefined) => void;
}) => {
  const [region, setRegion] = useState<string | undefined>();
  const [resourceGroup, setResourceGroup] = useState<string | undefined>();
  const [vnetName, setVnetName] = useState<string | undefined>();

  const handleRegionChange = (value?: string) => {
    setRegion(value);
    setResourceGroup(undefined);
    setVnetName(undefined);
  };

  const handleResourceGroupChange = (value: string) => {
    setResourceGroup(value);
    setVnetName(undefined);
  };

  const isComplete = !!region && !!resourceGroup && !!vnetName;

  const handleConfirm = () => {
    if (isComplete) {
      onClose({ region, vnetName, resourceGroup });
    }
  };

  return (
    <Popper anchorEl={anchorEl} disablePortal placement='bottom-start' open>
      <Paper
        sx={{
          width: '308px',
          padding: '24px',
          borderRadius: '4px',
          marginTop: '-33px',
          boxShadow: 'var(--mui-customShadows-lg)',
        }}
      >
        <Stack gap='24px'>
          <Typography>Choose a VNet</Typography>
          <RestoreRegionSelect
            restoreAccountId={accountId}
            regionName={region}
            onChange={handleRegionChange}
            cloudProvider={Provider.Azure}
            isRequired
          />
          <ResourceGroupSelect
            accountId={accountId}
            resourceGroup={resourceGroup}
            onChange={handleResourceGroupChange}
            includeAll
          />
          <AzureVnetSelect
            accountId={accountId}
            resourceGroup={resourceGroup}
            location={region}
            vnetName={vnetName}
            onChange={setVnetName}
          />
          <Stack direction='row' gap='8px' justifyContent='flex-end'>
            <Button onClick={() => onClose(undefined)}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              variant='contained'
              disabled={!isComplete}
            >
              Customize VNet
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Popper>
  );
};
